import React, { useState } from "react";
import {
    Button,
    Card,
    CardContent,
    Chip,
    Grid,
    Typography,
    useTheme,
    ThemeProvider
} from "@mui/material";
import ClientUserInfoModal from "../ClientUserInfoModal";
import SyncIcon from "../../images/SyncIcon";
import PackageIcon from "../../images/PackageIcon";
import LooksOneOutlinedIcon from "@mui/icons-material/LooksOneOutlined";
import {
    openManageBookingDialogAtom,
    selectedBookingAtom
} from "../../atoms/bookingsTable";
import {
    isMbxUserVerifiedStaff,
    mbxUserAtom,
    updateClientInVisibleBookingsAtom
} from "../../atoms/atoms";
import { useAtom } from "jotai";
import { auditClientNotesUpdate } from "../../modules/Audit";
import { graphql, graphqlOperation } from "../../modules/AmplifyServices";
import * as mutations from "../../graphql/mutations";
import moment from "moment-timezone";
import { DialogTheme } from "../../styles/DialogTheme";

const MobileBookingCard = ({
    booking,
    bookingReq = null,
    handleOpenVirtualBookingModal,
    type = "CONFIRMED",
    handleReqAccepted = null,
    handleReqDeclined = null
}) => {
    const [, setOpenManageBookingDialog] = useAtom(openManageBookingDialogAtom);
    const [, setSelectedBooking] = useAtom(selectedBookingAtom);
    const [preventManageBookingDialog, setPreventManageBookingDialog] =
        useState(false);
    const theme = useTheme();

    const handleOpenAddressLink = (address) => {
        window.open(
            `https://www.google.com/maps/search/?api=1&query=${address.replaceAll(
                " ",
                "+"
            )}`,
            "_blank"
        );
    };

    const handleBookingCardClick = () => {
        setSelectedBooking(booking);
        if (!preventManageBookingDialog) {
            setOpenManageBookingDialog(true);
        }
    };

    const renderBookingIcon = (orderType) => {
        const iconProps = { sx: { marginLeft: "9px", fontSize: "1rem" } };

        switch (orderType) {
            case "ONGOING":
                return <SyncIcon {...iconProps} />;
            case "SINGLE":
                return <LooksOneOutlinedIcon {...iconProps} />;
            default:
                return <PackageIcon {...iconProps} />;
        }
    };

    return (
        <Card
            sx={{
                backgroundColor: !booking.order && theme.palette.primary.light
            }}
        >
            <CardContent
                onClick={() => {
                    if (type !== "PENDING") handleBookingCardClick();
                }}
            >
                {type === "CONFIRMED" ? (
                    <ConfirmedBookingCardContent
                        booking={booking}
                        handleOpenVirtualBookingModal={
                            handleOpenVirtualBookingModal
                        }
                        handleOpenAddressLink={handleOpenAddressLink}
                        renderBookingIcon={renderBookingIcon}
                        setPreventManageBookingDialog={
                            setPreventManageBookingDialog
                        }
                    />
                ) : (
                    <PendingBookingCardContent
                        booking={booking}
                        handleOpenAddressLink={handleOpenAddressLink}
                        renderBookingIcon={renderBookingIcon}
                        handleReqAccepted={handleReqAccepted}
                        handleReqDeclined={handleReqDeclined}
                        bookingReq={bookingReq}
                    />
                )}
            </CardContent>
        </Card>
    );
};
export default MobileBookingCard;

//Card content for Upcoming (future) & Past Bookings
const ConfirmedBookingCardContent = ({
    booking,
    handleOpenVirtualBookingModal,
    handleOpenAddressLink,
    renderBookingIcon,
    setPreventManageBookingDialog
}) => {
    const [mbxUser] = useAtom(mbxUserAtom);
    const [openClientInfoDialog, setOpenClientInfoDialog] = useState(false);
    const [, updateClientInVisibleBookings] = useAtom(
        updateClientInVisibleBookingsAtom
    );
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const [originalNotes, setOriginalNotes] = useState("");

    const theme = useTheme();

    const handleClientNameClick = () => {
        setOpenClientInfoDialog(true);
        setPreventManageBookingDialog(true);
        setOriginalNotes(booking.clientNotes);
    };

    const handleCloseClientInfoDialog = () => {
        setOpenClientInfoDialog(false);
        setPreventManageBookingDialog(false);
    };

    let bookingNotes = "";

    if (booking && Object.keys(booking).length > 0) {
        const orderNotes = booking?.order?.Notes
            ? JSON.parse(booking.order.Notes)[0]?.notes
            : null;

        const bookingNotesContent = booking?.Notes
            ? booking.Notes[0]?.notes
            : null;

        if (bookingNotesContent) {
            bookingNotes = `"${bookingNotesContent}"`;
        } else if (orderNotes) {
            bookingNotes = `"${orderNotes}"`;
        }
    }

    const handleSaveClientNotes = async (id, updatedClientObj) => {
        const updatedBookingData = {
            clientId: booking.clientId,
            client: {
                ...booking.client,
                Notes: updatedClientObj.newNotes
                    ? JSON.stringify(updatedClientObj.newNotes)
                    : booking.client.Notes
            }
        };
        updateClientInVisibleBookings(updatedBookingData);

        if (updatedClientObj.newNotes) {
            booking.client.Notes = JSON.stringify(updatedClientObj.newNotes);
        }

        const updatedClient = await graphql(
            graphqlOperation(mutations.updateClient, {
                input: {
                    id: id,
                    Notes: JSON.stringify(updatedClientObj.newNotes)
                }
            })
        );

        if (
            updatedClient &&
            updatedClient.data &&
            updatedClient.data.updateClient
        ) {
            try {
                await auditClientNotesUpdate(
                    mbxUser,
                    updatedClient.data.updateClient,
                    updatedClientObj.newNotes.notes,
                    originalNotes
                );
            } catch (e) {
                console.log("Error while entering audit");
            }
        }
    };

    return (
        <>
            <Typography
                variant="link"
                onClick={(e) => {
                    e.stopPropagation();
                    handleClientNameClick();
                }}
            >
                {booking?.clientName}
            </Typography>
            <Typography variant="body2">
                {moment(booking?.bookingDate).format("ddd, MMM D, YYYY")}
            </Typography>
            <Typography variant="body2">
                {booking?.bookingTimeDesc
                    ? booking.bookingTimeDesc
                    : `${moment
                          .tz(booking?.startdate, booking?.timezone)
                          .format("h:mm A")} \u2014 ${moment
                          .tz(booking?.startdate, booking?.timezone)
                          .add(booking?.servicetype?.minutes, "minutes")
                          .format("h:mm A")}`}
            </Typography>
            <Typography
                variant="link"
                width={"fit-content"}
                onClick={(e) => {
                    e.stopPropagation();
                    booking.location.includes("Virtual Appointment")
                        ? handleOpenVirtualBookingModal(booking)
                        : handleOpenAddressLink(booking.location);
                }}
            >
                {booking?.location}
            </Typography>
            <Typography variant="body2">
                {booking?.serviceName}
                {renderBookingIcon(booking.orderType)}
            </Typography>
            {isVerifiedStaff && (
                <Typography variant="body2">
                    {`With: ${booking?.provider?.firstname} ${booking?.provider?.lastname}`}
                </Typography>
            )}
            {bookingNotes && (
                <Typography
                    variant="body2"
                    sx={{
                        fontStyle: "italic",
                        paddingTop: "18px",
                        whiteSpace: "break-spaces"
                    }}
                >
                    {bookingNotes}
                </Typography>
            )}
            <StatusChip status={booking?.status} />

            <ThemeProvider theme={DialogTheme(theme)}>
                <ClientUserInfoModal
                    mobileView={true}
                    isOpen={openClientInfoDialog}
                    onClose={handleCloseClientInfoDialog}
                    loggedInUser={mbxUser}
                    onSave={handleSaveClientNotes}
                    mode={"EDIT"}
                    focusNotes={true}
                    clientUser={{
                        id: booking.clientId,
                        registered: booking.client.user.registered,
                        userId: booking.client.userId,
                        userRole: booking.client.user.role,
                        firstName: booking.clientFirstName,
                        lastName: booking.clientLastName,
                        emailAddress: booking.clientEmail,
                        phone: booking.clientMobilePhone,
                        clientNotes: JSON.parse(booking.client.Notes),
                        addressOneLine: booking.client.user.addressoneline,
                        defaultPartialCC: booking.client.defaultpartialcc
                    }}
                />
            </ThemeProvider>
        </>
    );
};

//Card content for Pending Bookings
const PendingBookingCardContent = ({
    bookingReq,
    booking,
    handleOpenAddressLink,
    renderBookingIcon,
    handleReqAccepted = null,
    handleReqDeclined = null
}) => {
    const theme = useTheme();
    const [isVerifiedStaff] = useAtom(isMbxUserVerifiedStaff);
    const bookingNotes =
        booking && Object.keys(booking).length > 0 && booking.clientNotes
            ? `"${booking.clientNotes}"`
            : "";

    return (
        <>
            <Typography variant="subtitle2">
                {`${booking?.user?.firstname} ${booking?.user?.lastname}`}
            </Typography>
            {booking?.heldSlots.length > 0 &&
                booking?.heldSlots.map((slot, index) => (
                    <Typography key={index} variant="subtitle2">
                        {moment(slot?.startDate).format("ddd, MMM D, YYYY")}
                        {` - ${moment
                            .tz(
                                slot?.sdtutc,
                                booking?.provider?.timezone
                                    ? booking?.provider?.timezone
                                    : booking?.bookingTz
                            )
                            .format("h:mm A")} \u2014 ${moment
                            .tz(
                                slot?.sdtutc,
                                booking?.provider?.timezone
                                    ? booking?.provider?.timezone
                                    : booking?.bookingTz
                            )
                            .add(booking?.serviceType?.minutes, "minutes")
                            .format("h:mm A")}`}
                    </Typography>
                ))}
            <Typography
                variant="link"
                width={"fit-content"}
                sx={{
                    pointerEvents:
                        booking?.location.includes("Virtual Appointment") &&
                        "none"
                }}
                onClick={(e) => {
                    e.stopPropagation();
                    !booking?.location.includes("Virtual Appointment") &&
                        handleOpenAddressLink(booking?.location);
                }}
            >
                {booking?.location}
            </Typography>
            <Typography variant="body2">
                {booking?.serviceType?.name}
                {renderBookingIcon(
                    booking?.sdt === "forever"
                        ? "ONGOING"
                        : booking?.sdt === "single"
                          ? "SINGLE"
                          : "PACKAGE"
                )}
            </Typography>
            {isVerifiedStaff && (
                <Typography variant="body2">
                    {`With: ${booking?.provider?.firstname} ${booking?.provider?.lastname}`}
                </Typography>
            )}
            {bookingNotes && (
                <Typography
                    variant="body2"
                    sx={{
                        fontStyle: "italic",
                        paddingTop: "18px",
                        whiteSpace: "break-spaces"
                    }}
                >
                    {bookingNotes}
                </Typography>
            )}

            <Grid container spacing={1.1}>
                <Grid item xs={6}>
                    <Button
                        variant="pendingAction"
                        sx={{
                            backgroundColor: theme.palette.warning.main,
                            "&:hover": {
                                backgroundColor: theme.palette.warning.main
                            }
                        }}
                        onClick={() => {
                            handleReqDeclined(bookingReq);
                        }}
                    >
                        Decline
                    </Button>
                </Grid>
                <Grid item xs={6}>
                    <Button
                        variant="pendingAction"
                        sx={{
                            backgroundColor: theme.palette.success.main,
                            "&:hover": {
                                backgroundColor: theme.palette.success.main
                            }
                        }}
                        onClick={() => {
                            handleReqAccepted(bookingReq);
                        }}
                    >
                        Accept
                    </Button>
                </Grid>
            </Grid>
        </>
    );
};

const StatusChip = ({ status }) => {
    const theme = useTheme();

    //Default is "ARRIVED" Booking
    let chipProps = {
        label: status,
        sx: {
            backgroundColor: "rgba(0,0,0,0.1)",
            color: "rgba(0,0,0,0.5)",
            fontWeight: "700",
            width: "150px",
            alignSelf: "center",
            marginTop: "9px"
        }
    };

    switch (status) {
        case "SCHEDULED":
            chipProps = {
                ...chipProps,
                label: "SCHEDULED",
                sx: {
                    ...chipProps.sx,
                    backgroundColor: theme.palette.primary.light,
                    color: theme.palette.primary.dark
                }
            };
            break;
        case "CANCELLED":
            chipProps = {
                ...chipProps,
                label: "CANCELLED",
                sx: {
                    ...chipProps.sx,
                    backgroundColor: theme.palette.error.light,
                    color: theme.palette.common.white
                }
            };
            break;
        case "COMPLETED":
            chipProps = {
                ...chipProps,
                label: "COMPLETED",
                sx: {
                    ...chipProps.sx,
                    backgroundColor: theme.palette.success.light,
                    color: theme.palette.success.dark
                }
            };
            break;
        case "NOSHOW":
            chipProps = {
                ...chipProps,
                label: "NO-SHOW"
            };
            break;
        default:
            break;
    }

    return <Chip {...chipProps} />;
};
